import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Box, Typography, Paper, Button } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';

/**
 * Componente que protege rutas basándose en permisos de módulos
 * @param {object} props
 * @param {React.ReactNode} props.children - Componente a renderizar si tiene permiso
 * @param {string} props.module - Nombre del módulo requerido (ej: "comercial.objetivos")
 * @param {string} props.permission - Tipo de permiso requerido (view, create, edit, delete, approve)
 * @param {boolean} props.redirectToDashboard - Si true, redirige a dashboard en lugar de mostrar mensaje
 */
const ProtectedRoute = ({
  children,
  module,
  permission = 'view',
  redirectToDashboard = false
}) => {
  const { hasPermission, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // Mostrar loading mientras se verifica
  if (loading) {
    return null; // El suspense de App.js ya maneja el loading
  }

  // Si no está autenticado, redirigir a login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Verificar si tiene el permiso requerido
  const hasAccess = hasPermission(module, permission);

  // Si no tiene acceso
  if (!hasAccess) {
    // Opción 1: Redirigir al dashboard
    if (redirectToDashboard) {
      return <Navigate to="/" replace />;
    }

    // Opción 2: Mostrar mensaje de acceso denegado
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          p: 3,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            maxWidth: 500,
            textAlign: 'center',
            borderRadius: 3,
          }}
        >
          <LockIcon
            sx={{
              fontSize: 80,
              color: 'error.main',
              mb: 2,
            }}
          />
          <Typography variant="h5" gutterBottom fontWeight={600}>
            Acceso Denegado
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            No tienes permiso para acceder a este módulo.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Módulo requerido: <strong>{module}</strong>
            <br />
            Permiso requerido: <strong>{permission}</strong>
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.history.back()}
            sx={{ mr: 2 }}
          >
            Volver
          </Button>
          <Button
            variant="outlined"
            onClick={() => (window.location.href = '/')}
          >
            Ir al Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  // Si tiene acceso, renderizar el componente hijo
  return children;
};

export default ProtectedRoute;
